import { gql } from '@apollo/client'
import { useApolloClient, useMutation } from '@apollo/client/react'
import type * as React from 'react'
import { useRef, useState } from 'react'
import ActionQrDialog from '../components/ActionQrDialog'
import { GetCurrentUserDocument, SiteRole, UserLoginDocument } from '../gql/graphql'
import type { SiteInfo } from '../hooks'
import type { SelectedRole } from './RoleSelectionDialog'
import RoleSelectionDialog from './RoleSelectionDialog'

gql`
  mutation UserLogin($code: String, $openid: ID) {
    login(code: $code, openid: $openid) {
      ... on LoginResponse {
        authToken
        user {
          openid
          isAppAdmin
          ...BasicUserFields
          sites {
            id
            name
            roles
            avatar
          }
        }
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

interface LoginDialogProps {
  onLoginSuccess: (sites: SiteInfo[], role: SelectedRole, isAppAdmin: boolean) => void
  onClose?: () => void
}

function saveSelectedRole(role: SelectedRole) {
  localStorage.setItem('selectedRole', role.type)
  if (role.type === 'site_admin' || role.type === 'coach') {
    localStorage.setItem('currentSiteId', role.site.id)
  }
}

/**
 * A dialog to be used on a normal browser that prompts the user to log in by scanning
 * a weapp qrcode
 */
const LoginDialog: React.FC<LoginDialogProps> = ({ onLoginSuccess, onClose }) => {
  const apolloClient = useApolloClient()
  const hasLoggedIn = useRef(false)
  const [loginUser] = useMutation(UserLoginDocument)
  const [pendingLogin, setPendingLogin] = useState<{
    sites: SiteInfo[]
    isAppAdmin: boolean
    adminSites: SiteInfo[]
    coachSites: SiteInfo[]
  } | null>(null)

  const handleScanned = async (user: { id?: string | null; openid: string }) => {
    if (hasLoggedIn.current) return
    try {
      const { data } = await loginUser({ variables: { openid: user.openid } })

      const loginResult = data?.login
      if (!loginResult) {
        console.error('Login failed: No data received')
        return
      }

      if (loginResult.__typename === 'GraphqlRequestError') {
        console.error('Login failed:', loginResult.message)
        return
      }

      if (loginResult.__typename === 'LoginResponse') {
        localStorage.setItem('authToken', loginResult.authToken)

        apolloClient.writeQuery({
          query: GetCurrentUserDocument,
          data: {
            __typename: 'Query',
            // biome-ignore lint/suspicious/noExplicitAny: writeQuery type doesn't accept UserBasic
            currentUser: loginResult.user as any,
          },
        })

        hasLoggedIn.current = true

        const sites = loginResult.user.sites as SiteInfo[]
        const isAppAdmin = loginResult.user.isAppAdmin
        const adminSites = sites.filter(
          (s) =>
            s.roles.includes(SiteRole.Admin) || s.roles.includes(SiteRole.SuperAdmin),
        )
        const coachSites = sites.filter((s) => s.roles.includes(SiteRole.Coach))
        const roleCount = (isAppAdmin ? 1 : 0) + adminSites.length + coachSites.length

        if (roleCount > 1) {
          setPendingLogin({ sites, isAppAdmin, adminSites, coachSites })
          return
        }

        if (isAppAdmin) {
          const role: SelectedRole = { type: 'app_admin' }
          saveSelectedRole(role)
          onLoginSuccess(sites, role, isAppAdmin)
          return
        }

        if (adminSites.length === 1) {
          const role: SelectedRole = { type: 'site_admin', site: adminSites[0] }
          saveSelectedRole(role)
          onLoginSuccess(sites, role, isAppAdmin)
          return
        }

        if (coachSites.length === 1) {
          const role: SelectedRole = { type: 'coach', site: coachSites[0] }
          saveSelectedRole(role)
          onLoginSuccess(sites, role, isAppAdmin)
          return
        }

        // Regular member — default to site app (no role to save)
        const role: SelectedRole = {
          type: 'site_admin',
          site: sites[0] ?? { id: '', name: '', roles: [] },
        }
        onLoginSuccess(sites, role, isAppAdmin)
      }
    } catch (apolloError) {
      console.error('Apollo error:', apolloError)
    }
  }

  const handleRoleSelect = (role: SelectedRole) => {
    if (!pendingLogin) return
    saveSelectedRole(role)
    onLoginSuccess(pendingLogin.sites, role, pendingLogin.isAppAdmin)
  }

  const handleRoleSelectionClose = () => {
    localStorage.removeItem('authToken')
    hasLoggedIn.current = false
    setPendingLogin(null)
    onClose?.()
  }

  if (pendingLogin) {
    return (
      <RoleSelectionDialog
        isAppAdmin={pendingLogin.isAppAdmin}
        adminSites={pendingLogin.adminSites}
        coachSites={pendingLogin.coachSites}
        onSelect={handleRoleSelect}
        onClose={handleRoleSelectionClose}
      />
    )
  }

  return (
    <ActionQrDialog
      open
      onClose={onClose}
      action="login"
      title="微信扫码登录"
      onScanned={handleScanned}
    />
  )
}

export default LoginDialog
